/**
 * ONE TAP — round and match flow.
 *
 * Owns the phase machine described in DESIGN.md §2:
 *
 *   Lobby -> Warmup -> Live -> RoundEnd -> (Warmup | MatchEnd) -> Lobby
 *
 * `beginRound` is the single entry point that puts a world into play: the room
 * (Durable Object) calls it once to start a match, and this module calls it
 * again for every subsequent round. Everything here is deterministic — the only
 * randomness is spawn-point rotation, driven by a mulberry32 stream seeded from
 * the world seed and the round number, so server and client always agree.
 *
 * NOTE: this file runs inside a Cloudflare Worker. No `three`, no DOM.
 */

import {
  DASH_CHARGES,
  DASH_CHARGES_UNARMED,
  MATCH_END_MS,
  MAX_HEALTH,
  PLAYER_EYE_HEIGHT,
  ROUND_END_MS,
  ROUND_TIME_MS,
  ROUND_WARMUP_MS,
  ROUNDS_TO_WIN,
  SUDDEN_DEATH_MS,
  TEAM_A,
  TEAM_B,
  TEAM_SIZE_MAX,
} from '@shared/constants';
import { copy, hashString, makeRng, set, v3 } from '@shared/math';
import type { Vec3 } from '@shared/math';
import { GunState, HealthState, MoveState, RoundPhase } from '@shared/types';
import type {
  ArenaData,
  CollisionWorld,
  GameEvent,
  GunEntity,
  PlayerState,
  WorldState,
} from '@shared/types';

// ---------------------------------------------------------------------------
// World registry
//
// WorldState is a pure data bag on the wire (see types.ts) — it carries no seed
// and no back-pointer to the arena, but round setup needs both. Rather than
// widen the wire type, the two non-serialized facts live in WeakMaps keyed by
// the world object. `createWorld` registers them; `stepMatch` refreshes the
// arena every tick so a world rebuilt from a snapshot on the client picks it up
// as soon as it is stepped.
// ---------------------------------------------------------------------------

const worldSeeds = new WeakMap<WorldState, number>();
const worldArenas = new WeakMap<WorldState, ArenaData>();

/** Fallback seed for a world that was rebuilt from the wire and never registered. */
const ORPHAN_SEED = hashString('ONETAP');

/** Odd 32-bit constant (2^32 / golden ratio) — decorrelates the per-round seed. */
const ROUND_SEED_MIX = 0x9e3779b1 | 0;

/**
 * Guns 0 and 1 are the two team guns and they are the ONLY guns. DESIGN.md §2:
 * "There is never a third gun." Sudden death escalates *information* (HIGH NOON),
 * never firepower. This constant is what `pruneExtraGuns` enforces, so a world
 * restored from an older checkpoint can never smuggle one back in.
 *
 * It is also the invariant that lets `PlayerState.heldGun` be both an array index
 * and a gun id: with a fixed two-gun array, `guns[i].id === i` always holds.
 */
export const TEAM_GUN_COUNT = 2;

/** Called by `createWorld`. Binds the arena and the deterministic seed to a world instance. */
export function registerWorld(w: WorldState, arena: ArenaData, seed: number): void {
  worldSeeds.set(w, seed >>> 0);
  worldArenas.set(w, arena);
}

/** The arena this world is played on, or null if it was never registered. */
export function arenaOf(w: WorldState): ArenaData | null {
  return worldArenas.get(w) ?? null;
}

function seedOf(w: WorldState): number {
  const s = worldSeeds.get(w);
  return s === undefined ? ORPHAN_SEED : s;
}

/** A fresh deterministic stream for one round. Same inputs -> same spawns, everywhere. */
function roundRng(w: WorldState): () => number {
  return makeRng((seedOf(w) ^ Math.imul(w.match.round + 1, ROUND_SEED_MIX)) >>> 0);
}

// ---------------------------------------------------------------------------
// Entity factory (lives here so world.ts and the snapshot decoder agree)
// ---------------------------------------------------------------------------

export function createGunEntity(id: number, team: number): GunEntity {
  return {
    id,
    team,
    state: GunState.Grounded,
    holder: null,
    lastThrower: null,
    throwLockoutMs: 0,
    airborneMs: 0,
    throwOrigin: null,
    pos: v3(),
    vel: v3(),
    spin: v3(),
    rot: v3(),
    chambered: true,
    idleMs: 0,
    voidRespawnMs: 0,
  };
}

// ---------------------------------------------------------------------------
// Roster helpers
// ---------------------------------------------------------------------------

/**
 * Connected members of a team, ordered by id.
 *
 * The sort is not cosmetic: `Object.keys` order depends on insertion order, which
 * differs between a server that accepted joins one at a time and a client that
 * rebuilt the map from a snapshot. Sorting makes spawn assignment and "who is the
 * team leader" identical on both sides.
 */
function rosterOf(w: WorldState, team: number): PlayerState[] {
  const out: PlayerState[] = [];
  for (const id in w.players) {
    const p = w.players[id];
    if (p.connected && p.team === team) out.push(p);
  }
  out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}

function teamCount(w: WorldState, team: number): number {
  let n = 0;
  for (const id in w.players) {
    const p = w.players[id];
    if (p.connected && p.team === team) n++;
  }
  return n;
}

const isAlive = (p: PlayerState): boolean => p.connected && p.healthState !== HealthState.Dead;

/** Yaw that makes a player at `from` look at `to`. Convention: yaw 0 faces -Z (math.ts). */
function yawTowards(from: Vec3, to: Vec3): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  if (dx === 0 && dz === 0) return 0;
  return Math.atan2(-dx, -dz);
}

// ---------------------------------------------------------------------------
// Team assignment
// ---------------------------------------------------------------------------

/**
 * Pick the team a joining player should go on: whichever side is smaller, never
 * exceeding TEAM_SIZE_MAX. Returns -1 when both sides are full — the room is
 * expected to have already rejected the join via MAX_PLAYERS_PER_ROOM, so this
 * is a guard, not a normal path.
 */
export function assignTeam(w: WorldState): number {
  const a = teamCount(w, TEAM_A);
  const b = teamCount(w, TEAM_B);
  const aOpen = a < TEAM_SIZE_MAX;
  const bOpen = b < TEAM_SIZE_MAX;
  if (aOpen && bOpen) return a <= b ? TEAM_A : TEAM_B;
  if (aOpen) return TEAM_A;
  if (bOpen) return TEAM_B;
  return -1;
}

// ---------------------------------------------------------------------------
// Round setup
// ---------------------------------------------------------------------------

/**
 * Wipe every per-round bit of a player and stand them on a spawn pad facing the
 * middle of the map. Match-long stats (kills/deaths/catches/intercepts) survive.
 */
function resetPlayerForRound(p: PlayerState, spawn: Vec3, lookAt: Vec3): void {
  copy(p.pos, spawn);
  set(p.vel, 0, 0, 0);
  p.yaw = yawTowards(spawn, lookAt);
  p.pitch = 0;

  // Airborne rather than Grounded: the first movement step traces down and
  // settles them, which is correct whether or not the spawn pad sits exactly on
  // the floor. Claiming "grounded" without a ground trace would be a lie for one
  // tick and could eat a jump.
  p.moveState = MoveState.Airborne;
  p.healthState = HealthState.Healthy;
  p.health = MAX_HEALTH;

  p.grounded = false;
  p.crouching = false;
  p.wallNormal = null;
  p.wallSurfaceId = -1;
  p.wallRunElapsedMs = 0;

  p.dashCharges = DASH_CHARGES_UNARMED;
  p.dashCooldownMs = 0;
  p.dashElapsedMs = 0;
  set(p.dashDir, 0, 0, 0);

  p.slideElapsedMs = 0;
  // AIR jumps only. movement.ts refills this to 1 on every ground contact and
  // spends it on the mid-air jump, so the ground jump is not counted here — it is
  // gated by `grounded`/coyote time instead. Seeding 2 would hand out a free extra
  // air jump for the one tick before the spawn settles.
  p.jumpsRemaining = 1; // the one mid-air jump (DESIGN.md §3)
  p.coyoteMs = 0;
  p.jumpBufferMs = 0;

  p.grappleAnchorId = -1;
  p.grapplePoint = null;
  p.grappleLength = 0;
  p.grappleElapsedMs = 0;
  p.grappleCooldownMs = 0;
  p.grappleHookProgress = 0;

  p.heldGun = -1;
  p.fireCooldownMs = 0;
  p.throwChargeMs = 0;
  p.catchLockoutMs = 0;
  p.pulseCooldownMs = 0;
}

/**
 * Enforce the two-gun invariant, clearing any `heldGun` index that would be left
 * dangling past the end of the array.
 *
 * Nothing in this build ever creates a third gun (DESIGN.md §2), so this is a
 * guard rather than a routine cleanup: it exists so a world revived from a
 * Durable Object checkpoint written by an older build is normalised the moment a
 * round begins, instead of quietly desynchronising the snapshot encoder.
 */
function pruneExtraGuns(w: WorldState): void {
  if (w.guns.length <= TEAM_GUN_COUNT) return;
  for (const id in w.players) {
    const p = w.players[id];
    if (p.heldGun >= TEAM_GUN_COUNT) p.heldGun = -1;
  }
  w.guns.length = TEAM_GUN_COUNT;
}

/**
 * Park a gun, unheld, at a fixed point.
 *
 * It is left EMPTY on purpose. Only a clean mid-air catch chambers a round
 * (DESIGN.md §1) and `stepGun` force-unchambers any grounded gun every tick, so
 * parking one chambered would put a lie on the wire for exactly one tick — the
 * amber "loaded" glow flickering on a gun that is a paperweight. The round-start
 * chamber is granted in `resetGunsForRound`, to the leader who actually holds it.
 */
function parkGun(g: GunEntity, at: Vec3): void {
  g.state = GunState.Grounded;
  g.holder = null;
  g.lastThrower = null;
  g.throwLockoutMs = 0;
  g.airborneMs = 0;
  g.throwOrigin = null;
  copy(g.pos, at);
  set(g.vel, 0, 0, 0);
  set(g.spin, 0, 0, 0);
  set(g.rot, 0, 0, 0);
  g.chambered = false;
  g.idleMs = 0;
  g.voidRespawnMs = 0;
}

/**
 * Both team guns go back to their team, back to their spawn, chambered, and into
 * the hands of that team's leader (DESIGN.md §2). A team with nobody on it leaves
 * its gun lying at the spawn pad.
 */
function resetGunsForRound(w: WorldState, arena: ArenaData): void {
  for (let i = 0; i < TEAM_GUN_COUNT; i++) {
    const g = w.guns[i];
    if (!g) continue;
    const team = i === 0 ? TEAM_A : TEAM_B;
    g.team = team; // undo any re-tint from being stolen last round
    parkGun(g, team === TEAM_A ? arena.gunSpawnA : arena.gunSpawnB);

    const roster = rosterOf(w, team);
    const leader = roster.length > 0 ? roster[0] : null;
    if (leader) {
      // The one place in the game a chamber is granted without a catch: the round
      // starts with each team's gun loaded, in its leader's hands (DESIGN.md §2).
      g.holder = leader.id;
      g.state = GunState.HeldChambered;
      g.chambered = true;
      leader.heldGun = i;
      // Seed the position at the holder's eye line so the very first snapshot
      // does not show the gun sitting on its spawn pad for a frame.
      set(g.pos, leader.pos.x, leader.pos.y + PLAYER_EYE_HEIGHT, leader.pos.z);
    }
  }
}

/**
 * Start a round. Also starts a *match* when called from Lobby or MatchEnd, which
 * is how the room kicks things off: score and round counter reset, then round 1.
 */
export function beginRound(w: WorldState, events: GameEvent[]): void {
  const m = w.match;

  if (m.phase === RoundPhase.Lobby || m.phase === RoundPhase.MatchEnd) {
    m.round = 0;
    m.scoreA = 0;
    m.scoreB = 0;
    m.winner = -1;
    m.lastRoundWinner = -1;
  }

  m.round += 1;
  m.phase = RoundPhase.Warmup;
  m.phaseEndsAtMs = w.timeMs + ROUND_WARMUP_MS;
  m.roundEndsAtMs = w.timeMs + ROUND_WARMUP_MS + ROUND_TIME_MS;
  m.suddenDeath = false;

  // Guard the two-gun invariant before anything indexes into `w.guns`.
  pruneExtraGuns(w);

  const arena = arenaOf(w);
  if (arena) {
    const rng = roundRng(w);
    for (const team of [TEAM_A, TEAM_B]) {
      const roster = rosterOf(w, team);
      const spawns = team === TEAM_A ? arena.spawnsA : arena.spawnsB;
      // Rotate the starting index each round so the same player does not always
      // get the same pad — spread, but reproducible.
      const offset = spawns.length > 0 ? Math.floor(rng() * spawns.length) : 0;
      for (let i = 0; i < roster.length; i++) {
        const spawn = spawns.length > 0 ? spawns[(offset + i) % spawns.length] : arena.centerSpawn;
        resetPlayerForRound(roster[i], spawn, arena.centerSpawn);
      }
    }
    resetGunsForRound(w, arena);
  } else {
    // No arena bound (a world rebuilt from the wire that has not been stepped
    // yet). Still restore health and clear state so the round is playable the
    // moment geometry shows up; positions are whatever the last snapshot said.
    for (const id in w.players) {
      const p = w.players[id];
      if (!p.connected) continue;
      resetPlayerForRound(p, p.pos, p.pos);
    }
  }

  // Dash budget depends on being armed — the gunless player is the mobile threat.
  for (const id in w.players) {
    const p = w.players[id];
    if (!p.connected) continue;
    p.dashCharges = p.heldGun >= 0 ? DASH_CHARGES : DASH_CHARGES_UNARMED;
  }

  events.push({ t: 'roundstart', round: m.round });
}

// ---------------------------------------------------------------------------
// Round resolution
// ---------------------------------------------------------------------------

/** True when this team is holding a gun with a live round in it. */
function teamHasChamberedGun(w: WorldState, team: number): boolean {
  for (let i = 0; i < w.guns.length; i++) {
    const g = w.guns[i];
    if (!g.chambered || g.state !== GunState.HeldChambered || g.holder === null) continue;
    const holder = w.players[g.holder];
    if (holder && holder.team === team && isAlive(holder)) return true;
  }
  return false;
}

function endRound(
  w: WorldState,
  winner: number,
  reason: 'elimination' | 'timeout' | 'draw',
  events: GameEvent[],
): void {
  const m = w.match;
  m.phase = RoundPhase.RoundEnd;
  m.phaseEndsAtMs = w.timeMs + ROUND_END_MS;
  m.lastRoundWinner = winner;
  if (winner === TEAM_A) m.scoreA += 1;
  else if (winner === TEAM_B) m.scoreB += 1;
  events.push({ t: 'roundend', winner, reason });
}

function stepLive(w: WorldState, events: GameEvent[]): void {
  const m = w.match;

  // HIGH NOON (DESIGN.md §2). SUDDEN_DEATH_MS before the clock expires, every gun
  // in the arena lights a beacon visible through geometry and its hum goes loud
  // and global. Hiding stops working and the round resolves itself.
  //
  // The sim's entire contribution is this flag and this event: `match.suddenDeath`
  // is what the VFX beacons and the audio engine's global-hum mode read, and the
  // event is what fires the stinger and the banner. There is deliberately NO third
  // gun — an earlier draft dropped a neutral chambered gun at centre to "break
  // stalls", and DESIGN.md §2 explicitly repudiates it: a team can never actually
  // be disarmed, so the stall it solved does not exist. Sudden death escalates
  // information, never firepower.
  if (!m.suddenDeath && w.timeMs >= m.roundEndsAtMs - SUDDEN_DEATH_MS) {
    m.suddenDeath = true;
    events.push({ t: 'suddendeath' });
  }

  let countA = 0;
  let countB = 0;
  let aliveA = 0;
  let aliveB = 0;
  for (const id in w.players) {
    const p = w.players[id];
    if (!p.connected) continue;
    if (p.team === TEAM_A) {
      countA++;
      if (p.healthState !== HealthState.Dead) aliveA++;
    } else if (p.team === TEAM_B) {
      countB++;
      if (p.healthState !== HealthState.Dead) aliveB++;
    }
  }

  // An empty arena has no round to decide.
  if (countA === 0 && countB === 0) return;

  if (aliveA === 0 || aliveB === 0) {
    // Both wiped on the same tick (a trade, or a shared fall) is a draw.
    const winner = aliveA === aliveB ? -1 : aliveA > 0 ? TEAM_A : TEAM_B;
    endRound(w, winner, winner < 0 ? 'draw' : 'elimination', events);
    return;
  }

  if (w.timeMs >= m.roundEndsAtMs) {
    // DESIGN.md §2 tiebreak chain: more alive -> holding a chambered gun -> draw.
    let winner = -1;
    if (aliveA > aliveB) winner = TEAM_A;
    else if (aliveB > aliveA) winner = TEAM_B;
    else {
      const chamberedA = teamHasChamberedGun(w, TEAM_A);
      const chamberedB = teamHasChamberedGun(w, TEAM_B);
      if (chamberedA && !chamberedB) winner = TEAM_A;
      else if (chamberedB && !chamberedA) winner = TEAM_B;
    }
    endRound(w, winner, winner < 0 ? 'draw' : 'timeout', events);
  }
}

// ---------------------------------------------------------------------------
// The phase machine
// ---------------------------------------------------------------------------

export function stepMatch(w: WorldState, world: CollisionWorld, events: GameEvent[]): void {
  // Keep the arena binding fresh — cheap, and it makes a client-side world that
  // was rebuilt from snapshots self-healing.
  if (worldArenas.get(w) !== world.arena) worldArenas.set(w, world.arena);

  const m = w.match;

  switch (m.phase) {
    case RoundPhase.Lobby:
      // Inert. The room decides when there are enough players and calls beginRound.
      return;

    case RoundPhase.Warmup:
      if (w.timeMs >= m.phaseEndsAtMs) {
        m.phase = RoundPhase.Live;
        // Re-anchor the clock to the moment the round actually goes live, so a
        // long warmup (or a paused/hibernated room) never eats round time.
        m.roundEndsAtMs = w.timeMs + ROUND_TIME_MS;
        m.phaseEndsAtMs = m.roundEndsAtMs;
      }
      return;

    case RoundPhase.Live:
      stepLive(w, events);
      return;

    case RoundPhase.RoundEnd:
      if (w.timeMs >= m.phaseEndsAtMs) {
        if (m.scoreA >= ROUNDS_TO_WIN || m.scoreB >= ROUNDS_TO_WIN) {
          m.winner = m.scoreA >= ROUNDS_TO_WIN ? TEAM_A : TEAM_B;
          m.phase = RoundPhase.MatchEnd;
          m.phaseEndsAtMs = w.timeMs + MATCH_END_MS;
          m.roundEndsAtMs = w.timeMs;
          events.push({ t: 'matchend', winner: m.winner });
        } else {
          beginRound(w, events);
        }
      }
      return;

    case RoundPhase.MatchEnd:
      if (w.timeMs >= m.phaseEndsAtMs) {
        m.phase = RoundPhase.Lobby;
        m.phaseEndsAtMs = w.timeMs;
        m.roundEndsAtMs = w.timeMs;
        m.suddenDeath = false;
        pruneExtraGuns(w);
      }
      return;
  }
}
